import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Formik } from "formik";
import moment from "moment";
import { CircularProgress, IconButton, InputAdornment, MenuItem, useMediaQuery } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import CustomInput from "../../components/CustomInput";
import CouponInput from "../../components/CouponInput";
import FakeInputAutofill from "../../components/FakeInputAutofill";
import CustomRadioGroupButtons from "../../components/CustomRadioGroupButtons";
import CustomDatePicker from "../../components/CustomDatePicker";
import * as auth from "../../store/ducks/auth.duck";
import { buscaCep, createFormikValidation, fetchCitiesByUF, formatCNPJInput, formatCPFInput, formatCPForCNPJInput, formatIntegerInput, formatNumeroCartao, formatTelefoneInput, getCardFlag, limparMoeda, listaUfs } from "../../utils/Utils";
import CustomSelect from "../../components/CustomSelect";
import CustomAutocomplete from "../../components/CustomAutocomplete";
import BetterCircularProgress from "../../components/BetterCircularProgress";
import SwAlert from "../../components/SwAlert";
import { getPlans, signin } from "../../crud/auth.crud";
import { PlanCard } from "../home/financeiro/FinanceiroUtils";

const Signin = () => {
    const [loadingCEP, setLoadingCEP] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [plans, setPlans] = useState([]);
    const [success, setSuccess] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [urlCouponCode] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("coupon")?.toUpperCase() ?? "";
    });

    const history = useHistory();
    const isMobileScreen = useMediaQuery("(max-width: 576px)");
    const isMediumScreen = useMediaQuery("(max-width: 1024px)");

    const getDiscountedPrice = (price) => {
        if (!appliedCoupon) return price;
        if (appliedCoupon.cpd_type === "PERCENTAGE") {
            return Math.max(0, price - (price * appliedCoupon.cpd_value) / 100);
        }
        return Math.max(0, price - appliedCoupon.cpd_value);
    };

    const displayPlans = plans.map((plan) => ({
        ...plan,
        originalPrice: appliedCoupon ? plan.price : undefined,
        price: getDiscountedPrice(plan.price),
    }));

    const onSubmit = (values) => {
        setSubmitting(true);
        signin({
            ...values,
            city: values.city?.nome ?? values.city,
            card_due_date: moment(values.card_due_date).format("YYYY-MM"),
            coupon: appliedCoupon?.id ?? undefined,
        })
            .then(() => {
                setSuccess(values.email);
            })
            .catch((err) => {
                SwAlert({
                    title: err?.response?.data?.errors?.[0] ?? `Parece que houve um problema`,
                    text: err?.response?.data?.errors ? null : "Por favor, entre em contato com o suporte.",
                    customClass: {
                        content: err?.response?.data?.errors ? "p-0 pb-32" : "",
                    },
                    confirmButtonText: "Fechar",
                    icon: "error",
                });
            })
            .finally(() => setSubmitting(false));
    };

    const getInitialValues = () => {
        return {
            plan: "",
            person_type: "person",
            cpf: "",
            name: "",
            cnpj: "",
            corporate_name: "",
            fantasy_name: "",
            email: "",
            phone: "",
            card_number: "",
            card_due_date: null,
            card_cvv: "",
            card_name: "",
            card_cpf_cnpj: "",
            postalcode: "",
            address: "",
            number: "",
            neighborhood: "",
            city: "",
            uf: "",
        };
    };

    const validate = (values) => {
        const map = new Map();

        map.set("plan", "Selecione um plano");
        map.set("person_type", "Selecione o tipo de conta");

        if (values.person_type === "person") {
            map.set("cpf", "CPF");
            map.set("name", "Nome completo");
        } else {
            map.set("cnpj", "CNPJ");
            map.set("corporate_name", "Razão social");
            map.set("fantasy_name", "Nome fantasia");
        }

        map.set("email", "E-mail");
        map.set("phone", "Telefone");
        map.set("card_number", "Número do cartão");
        map.set("card_due_date", "Data de vencimento");
        map.set("card_cvv", "CVV");
        map.set("card_name", "Nome do titular");
        map.set("card_cpf_cnpj", "CPF/CNPJ do titular");
        map.set("postalcode", "CEP");
        map.set("address", "Logradouro");
        map.set("number", "Número");
        map.set("neighborhood", "Bairro");
        map.set("city", "Cidade");
        map.set("uf", "Estado");

        const errors = createFormikValidation(map, values);

        if (!moment(values.card_due_date, "MM/YYYY").isValid()) {
            errors.card_due_date = "Data inválida";
        } else if (moment(values.card_due_date, "MM/YYYY").isBefore(moment(moment().format("MM/YYYY"), "MM/YYYY"))) {
            errors.card_due_date = "Data passada";
        }

        return errors;
    };

    const onSearchCities = (uf) => {
        setCitiesLoading(true);

        fetchCitiesByUF(
            uf,
            (loading) => setCitiesLoading(loading),
            (data) => setCities(data),
        );
    };

    useEffect(() => {
        getPlans()
            .then((res) => {
                setPlans(res.data);
            })
            .catch((err) => {
                SwAlert({
                    title: err?.response?.data?.errors ? err?.response?.data?.errors[0] : `Parece que houve um problema.`,
                    text: err?.response?.data?.errors ? null : "Por favor, entre em contato com o suporte.",
                    customClass: {
                        content: err?.response?.data?.errors ? "p-0 pb-32" : "",
                    },
                    confirmButtonText: "Fechar",
                    icon: "error",
                }).finally(() => history.push("/"));
            })
            .finally(() => setLoadingPlans(false));
    }, []);

    if (success) {
        return (
            <div className="signinSuccessContainer">
                <img src="images/Illustration_Success.svg" alt="success" width={104} />

                <div className="d-flex flex-column gap-12 align-items-center text-center">
                    <span className="tt-24 semibold color-white">Bem-vindo(a) ao Oliver!</span>
                    <span className="tt-16 color-dark-6">
                        Seu cadastro foi realizado e seu plano foi ativado. Para acessar a plataforma, utilize a senha enviada no e-mail <b>{success}</b>
                    </span>
                </div>

                <button type="button" className="btn btn-primary btn-large tt-16 medium color-white w-100" onClick={() => history.push("/")}>
                    Começar a usar
                </button>
            </div>
        );
    }

    return (
        <Formik initialValues={getInitialValues()} validate={validate} onSubmit={onSubmit}>
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setTouched }) => (
                <form id="signin-form" noValidate onSubmit={handleSubmit} className="signinContainer d-flex flex-column gap-56" style={{ paddingBlock: "56px 40px" }}>
                    <FakeInputAutofill />

                    <div className="signinHeader d-flex flex-column gap-40 pil-56 pb-56">
                        <div>
                            <IconButton onClick={() => history.push("/login")} className="p-0">
                                <div className="d-flex gap-8 align-items-center color-dark-6">
                                    <ArrowBackIosIcon style={{ color: "#9CA3AF", fontSize: 14 }} />

                                    <span className="tt-16 medium">Voltar</span>
                                </div>
                            </IconButton>
                        </div>

                        <div className="d-flex flex-column gap-16">
                            <span className={`${isMobileScreen ? "tt-32" : "tt-40"} semibold color-white`}>Cadastre-se no OliverAI</span>

                            <span className="tt-20 color-dark-6">Selecione o plano ideal para você e faça seu cadastro abaixo.</span>
                        </div>

                        <BetterCircularProgress loading={loadingPlans}>
                            <CustomRadioGroupButtons
                                name="plan"
                                items={displayPlans.map((plan) => ({
                                    value: plan.id,
                                    onChange: () => {
                                        setFieldValue("plan", plan.id);
                                    },
                                    label: <PlanCard plan={plan} coupon={appliedCoupon} isMobileScreen={isMobileScreen} />,
                                }))}
                                helperText={touched.plan && errors.plan && "Selecione um plano"}
                                error={Boolean(touched.plan && errors.plan)}
                                value={values.plan}
                                disabled={submitting}
                                fullWidthItem={isMediumScreen}
                                wrap3
                                radius8
                                variant="plan"
                            />

                            <CouponInput
                                appliedCoupon={appliedCoupon}
                                onCouponChange={setAppliedCoupon}
                                disabled={submitting}
                                isMobileScreen={isMobileScreen}
                                initialCode={urlCouponCode}
                            />
                        </BetterCircularProgress>
                    </div>

                    <div className="signinData pil-56 pb-56 d-flex flex-column gap-40">
                        <span className="tt-24 semibold color-white">Seus dados</span>

                        <CustomRadioGroupButtons
                            name="person_type"
                            items={[
                                {
                                    label: "Pessoa física",
                                    value: "person",
                                    onChange: (e) => {
                                        setFieldValue("person_type", e.target.value);
                                        setFieldValue("corporate_name", "");
                                        setFieldValue("fantasy_name", "");
                                        setFieldValue("cnpj", "");
                                    },
                                },
                                {
                                    label: "Pessoa jurídica",
                                    value: "company",
                                    onChange: (e) => {
                                        setFieldValue("person_type", e.target.value);
                                        setFieldValue("name", "");
                                        setFieldValue("cpf", "");
                                    },
                                },
                            ]}
                            helperText={touched.person_type && errors.person_type}
                            error={Boolean(touched.person_type && errors.person_type)}
                            value={values.person_type}
                            disabled={submitting}
                            fullWidthItem={isMediumScreen}
                            variant="tiny"
                            radius8
                        />

                        <div className="d-flex flex-wrap gap-24">
                            {values.person_type === "person" ? (
                                <>
                                    <div className="wrap-2">
                                        <CustomInput placeholder="Informe seu nome completo" label="Nome completo" name="name" value={values.name} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                                    </div>
                                    <div className="wrap-2">
                                        <CustomInput placeholder="Informe seu CPF" label="CPF" name="cpf" value={values.cpf} handleBlur={handleBlur} handleChange={(e) => handleChange(formatCPFInput(e))} errors={errors} touched={touched} disabled={submitting} />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="wrap-1">
                                        <CustomInput placeholder="Informe a razão social" label="Razão social" name="corporate_name" value={values.corporate_name} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                                    </div>
                                    <div className="wrap-1">
                                        <CustomInput placeholder="Informe o nome fantasia" label="Nome fantasia" name="fantasy_name" value={values.fantasy_name} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                                    </div>
                                    <div className="wrap-1">
                                        <CustomInput placeholder="Informe o CNPJ" label="CNPJ" name="cnpj" value={values.cnpj} handleBlur={handleBlur} handleChange={(e) => handleChange(formatCNPJInput(e))} errors={errors} touched={touched} disabled={submitting} />
                                    </div>
                                </>
                            )}

                            <div className="wrap-2">
                                <CustomInput placeholder="Informe seu e-mail" label="E-mail" name="email" value={values.email} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                            </div>

                            <div className="wrap-2">
                                <CustomInput placeholder="Informe seu telefone" label="Telefone" name="phone" value={values.phone} handleBlur={handleBlur} handleChange={(e) => handleChange(formatTelefoneInput(e))} errors={errors} touched={touched} disabled={submitting} />
                            </div>
                        </div>
                    </div>

                    <div className="signinPaymentData pil-56 pb-56 d-flex flex-column gap-40">
                        <span className="tt-24 semibold color-white">Dados de pagamento</span>

                        <div className="d-flex flex-wrap gap-24">
                            <div className="wrap-1">
                                <CustomInput
                                    label="Número do cartão"
                                    placeholder="Informe o número do cartão"
                                    name="card_number"
                                    value={values.card_number}
                                    handleBlur={handleBlur}
                                    handleChange={(e) => handleChange(formatNumeroCartao(e))}
                                    touched={touched}
                                    errors={errors}
                                    disabled={submitting}
                                    InputProps={{
                                        startAdornment: getCardFlag(limparMoeda(values.card_number ?? "")) ? (
                                            <InputAdornment position="start">
                                                <div className="d-flex align-items-center justify-content-center" style={{ width: 35, height: 25, padding: 2, backgroundColor: "#fff", borderRadius: 4 }}>
                                                    <img className="w-100" src={`/icons/cardflag/${getCardFlag(limparMoeda(values.card_number))}.png`} alt={getCardFlag(limparMoeda(values.card_number))} />
                                                </div>
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                    inputProps={{
                                        maxLength: 23,
                                    }}
                                />
                            </div>

                            <div className="wrap-2">
                                <CustomDatePicker
                                    views={["month", "year"]}
                                    mask="__/____"
                                    format="MM/yyyy"
                                    name="card_due_date"
                                    value={values.card_due_date}
                                    placeholder={moment().format("MM/YYYY")}
                                    label="Data de vencimento"
                                    handleBlur={() => setTouched({ ...touched, card_due_date: true })}
                                    handleChange={(e) => {
                                        setFieldValue("card_due_date", e);
                                    }}
                                    touched={touched}
                                    errors={errors}
                                    disabled={submitting}
                                    minDate={moment().toDate()}
                                />
                            </div>

                            <div className="wrap-2">
                                <CustomInput
                                    label="CVV"
                                    placeholder="Informe o CVV"
                                    name="card_cvv"
                                    value={values.card_cvv}
                                    handleBlur={handleBlur}
                                    handleChange={(e) => handleChange(formatIntegerInput(e))}
                                    touched={touched}
                                    errors={errors}
                                    disabled={submitting}
                                    inputProps={{
                                        maxLength: 4,
                                    }}
                                />
                            </div>

                            <div className="wrap-2">
                                <CustomInput placeholder="Informe o CPF/CNPJ do titular" label="CPF/CNPJ do titular" name="card_cpf_cnpj" value={values.card_cpf_cnpj} handleBlur={handleBlur} handleChange={(e) => handleChange(formatCPForCNPJInput(e))} errors={errors} touched={touched} disabled={submitting} />
                            </div>

                            <div className="wrap-2">
                                <CustomInput placeholder="Informe o nome do titular igual ao do cartão" label="Nome do titular" name="card_name" value={values.card_name} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                            </div>
                        </div>
                    </div>

                    <div className="signinPaymentData pil-56 pb-56 d-flex flex-column gap-40">
                        <span className="tt-24 semibold color-white">Endereço de cobrança</span>

                        <div className="d-flex flex-wrap gap-24">
                            <div className="wrap-1">
                                <CustomInput
                                    placeholder="CEP"
                                    label="Informe o CEP"
                                    name="postalcode"
                                    value={values.postalcode}
                                    handleBlur={handleBlur}
                                    handleChange={(e) =>
                                        handleChange(
                                            buscaCep(e, setFieldValue, "address", "neighborhood", "city", "uf", (onLoading) => {
                                                setLoadingCEP(onLoading);
                                                setFieldValue("number", "");
                                            }),
                                        )
                                    }
                                    errors={errors}
                                    touched={touched}
                                    disabled={submitting}
                                    loadingCEP={loadingCEP}
                                />
                            </div>

                            <div className="wrap-1">
                                <CustomInput placeholder="Informe o logradouro" label="Logradouro" name="address" value={values.address} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                            </div>

                            <div className="wrap-2">
                                <CustomInput placeholder="Informe o número" label="Número" name="number" value={values.number} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                            </div>

                            <div className="wrap-2">
                                <CustomInput placeholder="Informe o bairro" label="Bairro" name="neighborhood" value={values.neighborhood} handleBlur={handleBlur} handleChange={handleChange} errors={errors} touched={touched} disabled={submitting} />
                            </div>

                            <div className="wrap-2">
                                <CustomSelect
                                    label="Estado"
                                    placeholder="Selecione o estado"
                                    name="uf"
                                    value={values.uf}
                                    handleBlur={handleBlur}
                                    handleChange={(e) => {
                                        handleChange(e);
                                        setFieldValue("city", "");
                                    }}
                                    touched={touched}
                                    errors={errors}
                                >
                                    {listaUfs.map((option, idx) => (
                                        <MenuItem key={`uf${idx}`} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            </div>

                            <div className="wrap-2">
                                <CustomAutocomplete
                                    freeSolo
                                    placeholder={values.uf ? "Informe ou selecione a cidade" : ""}
                                    name="city"
                                    value={values.city}
                                    noOptionsText="Sem resultados."
                                    label="Cidade"
                                    handleFocus={() => onSearchCities(values.uf)}
                                    handleChange={(_, e) => setFieldValue("city", e)}
                                    handleInputChange={(e) => (typeof e?.target?.value === "string" ? setFieldValue("city", e.target.value) : {})}
                                    options={cities.filter((c) => (c.nome && typeof values.city === "string" ? c.nome.toLowerCase().includes(values.city.toLowerCase()) : true))}
                                    disabled={submitting || !values.uf}
                                    errors={errors}
                                    getOptionLabel={(option) => (option ? (option.nome ?? option) : "")}
                                    getOptionSelected={(option) => (option ? (option.nome ?? option) : "")}
                                    touched={touched}
                                    loading={citiesLoading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pil-56 d-flex gap-16 justify-content-end" style={{ marginTop: -16 }}>
                        <button type="button" disabled={submitting} onClick={() => history.push("/")} className="btn btn-border btn-large tt-16 medium">
                            Cancelar
                        </button>

                        <button type="submit" form="signin-form" disabled={submitting} className="btn btn-primary btn-large tt-16 medium">
                            {submitting ? <CircularProgress size={14} className="margin-icon" style={{ color: "#fff" }} /> : ""}Cadastrar e confirmar pagamento
                        </button>
                    </div>
                </form>
            )}
        </Formik>
    );
};

export default connect(null, auth.actions)(Signin);
